import { createAsyncThunk } from '@reduxjs/toolkit';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/resources/resource.response.type';
import * as Request from '@shared/models/resources/resource.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setResourceCategories, setTrackResourceCategories } from '../slices/resourceCategory.slice';
import { setResources, setResourceFilterOptions } from '../slices/resource.slice';

export const ResourceEndpoints = {
    getResourceCategories: config.base_url + '/resources',
    getResourcesByCategory: config.base_url + '/resources/:categoryId',
    addRemoveFavourite: config.base_url + '/resources/fav',
    unlockResource: (resourceId: string) => config.base_url + '/resources/unlock/' + resourceId,
};

export const fetchResourceCategories = createAsyncThunk<void, Request.IResourceCategoryQueryType>(
    ResourceEndpoints.getResourceCategories,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.ResourceCategoryResponseType>>(
                `${ResourceEndpoints.getResourceCategories}?search=${query.params.search}`
            );

            dispatch(setResourceCategories(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching data from query ${ResourceEndpoints.getResourceCategories}`),
                { type: 'error' }
            );
        }
    }
);

export const fetchTrackResourceCategories = createAsyncThunk<void, Request.IResourceCategoryQueryType>(
    ResourceEndpoints.getResourceCategories,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.ResourceCategoryResponseType>>(
                `${ResourceEndpoints.getResourceCategories}?search=${query.params.search}&tracks=true`
            );

            dispatch(setTrackResourceCategories(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching data from query ${ResourceEndpoints.getResourceCategories}`),
                { type: 'error' }
            );
        }
    }
);

export const fetchResourcesByCategory = createAsyncThunk<void, Request.IResourceQueryType>(
    ResourceEndpoints.getResourcesByCategory,
    async (query, { dispatch }) => {
        try {
            const {
                categoryId,
                filters: { search, skills, fav, type },
            } = query.params;
            const queryParams = new URLSearchParams({ type });
            if (fav) queryParams.append('fav', fav);
            if (search && search.length > 0) queryParams.append('search', search);
            if (skills && skills.length > 0) queryParams.append('skills', skills);
            // if (gradeLevel && gradeLevel.length > 0)
            //     queryParams.append('gradeLevel', JSON.stringify(gradeLevel.split(',').map((i) => +i)));
            const res = await httpService.get<AscResponse<Response.ResourceResponseType>>(
                `${ResourceEndpoints.getResourceCategories}/${categoryId}?${queryParams}`
            );

            dispatch(setResources(res.data.data.resources));
            dispatch(setResourceFilterOptions(res.data.data.filterOptions));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(err, `Error fetching data from query ${ResourceEndpoints.getResourcesByCategory}`),
                { type: 'error' }
            );
        }
    }
);

export const addRemoveFavorite = createAsyncThunk<void, Request.IResourceAddRemoveFavType>(
    ResourceEndpoints.addRemoveFavourite,
    async (query, { dispatch }) => {
        try {
            const { resourceId, type } = query.params;
            const res = await httpService.get<AscResponse<{ isAdded: boolean }>>(
                `${ResourceEndpoints.addRemoveFavourite}/${resourceId}?type=${type}`
            );

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error add/remove favorite resource from query ${ResourceEndpoints.addRemoveFavourite}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const unlockResourceQuery = createAsyncThunk<void, Request.IResourceUnlockQueryType>(
    ResourceEndpoints.unlockResource('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.post<AscResponse<{ isAdded: boolean }>>(
                ResourceEndpoints.unlockResource(query.params.resourceId)
            );

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error unlocking a resource, from query ${ResourceEndpoints.unlockResource('id')}`
                ),
                { type: 'error' }
            );
        }
    }
);
